import * as React from "react";
import {inject, observer} from "mobx-react";
import type {SpaceViewRenderer} from "../../../../modules/space/spaceeditor/logic3d/renderers/SpaceViewRenderer";
import type {AppState} from "../../../../../data/state/AppState";
import {BoundarySpaceMap} from "../../../../../data/models/BoundarySpaceMap";
import {Xyicon} from "../../../../../data/models/Xyicon";
import {Markup} from "../../../../../data/models/Markup";
import {ObjectUtils} from "../../../../../utils/data/ObjectUtils";
import type {BoundaryDto, BoundarySpaceMapDto, MarkupDto, XyiconDto} from "../../../../../generated/api/base";
import type {Pointer} from "../../../../../utils/interaction/Pointer";
import {MathUtils} from "../../../../../utils/math/MathUtils";
import {THREEUtils} from "../../../../../utils/THREEUtils";
import {PointerDetectorReact} from "../../../../interaction/PointerDetectorReact";
import {Constants} from "../../../../modules/space/spaceeditor/logic3d/Constants";
import {NavigationCameraBoxV5} from "./NavigationCameraBoxV5";

interface INavigationBoxProps {
	readonly spaceViewRenderer: SpaceViewRenderer;
	readonly navigationBoxImgSrc: string;
	readonly appState?: AppState;
}

@inject("appState")
@observer
export class NavigationBoxV5 extends React.Component<INavigationBoxProps> {
	private readonly _background = React.createRef<HTMLDivElement>();
	private _ghostXyicons: Xyicon[] = [];
	private _ghostBoundaries: BoundarySpaceMap[] = [];
	private _ghostMarkups: Markup[] = [];

	private copyGhostItems = () => {
		const postFix = "_ghost";

		this._ghostXyicons = this.props.spaceViewRenderer.ghostModeManager.ghostXyicons.map((xyicon3D) => {
			const clonedData = ObjectUtils.deepClone(xyicon3D.modelData.data) as XyiconDto;

			clonedData.xyiconID += postFix;

			return new Xyicon(clonedData as XyiconDto, this.props.appState);
		});

		this._ghostBoundaries = this.props.spaceViewRenderer.ghostModeManager.ghostBoundaries.map((boundary) => {
			const clonedData = ObjectUtils.deepClone(boundary.modelData.data) as BoundarySpaceMapDto;

			clonedData.boundarySpaceMapID += postFix;

			return new BoundarySpaceMap(clonedData as BoundaryDto, (boundary.modelData as BoundarySpaceMap).parent);
		});

		this._ghostMarkups = this.props.spaceViewRenderer.ghostModeManager.ghostMarkups.map((markup3D) => {
			const clonedData = ObjectUtils.deepClone(markup3D.modelData.data) as MarkupDto;

			clonedData.markupID += postFix;

			return new Markup(clonedData as MarkupDto, this.props.appState);
		});
	};

	private onPointerDown = (pointer: Pointer) => {
		this.copyGhostItems();

		this.moveCameraTo(pointer.localX, pointer.localY);
	};

	private getBackgroundSize() {
		const spaceSize = this.props.spaceViewRenderer.spaceSize;
		const background = this._background.current;
		let width: number = 0;
		let height: number = 0;

		if (background) {
			width = background.offsetWidth;
			height = background.offsetHeight;

			if (spaceSize && spaceSize.width > 0 && spaceSize.height > 0) {
				if (this.getSpaceToNavigationBoxRatio(background) > 1) {
					width = background.offsetWidth;
					height = width * (spaceSize.height / spaceSize.width);
				} else {
					height = background.offsetHeight;
					width = height * (spaceSize.width / spaceSize.height);
				}
			}
		}

		return {
			bgImageWidth: width,
			bgImageHeight: height,
		};
	}

	private onPointerMove = (pointer: Pointer) => {
		this.moveCameraTo(pointer.localX, pointer.localY);
	};

	private onPointerUp = () => {
		this.props.spaceViewRenderer.ghostModeManager.start(this._ghostXyicons, this._ghostBoundaries, this._ghostMarkups);
	};

	private moveCameraTo(x: number, y: number) {
		const background = this._background.current;
		const {bgImageWidth, bgImageHeight} = this.getBackgroundSize();
		const ratioOffset = {
			x: (background.offsetWidth - bgImageWidth) / 2,
			y: (background.offsetHeight - bgImageHeight) / 2,
		};
		const posOnImage = {
			x: MathUtils.clamp(x, ratioOffset.x, ratioOffset.x + bgImageWidth),
			y: MathUtils.clamp(y, ratioOffset.y, ratioOffset.y + bgImageHeight),
		};

		const posToNDC = {
			x: MathUtils.getInterpolation(posOnImage.x, ratioOffset.x, ratioOffset.x + bgImageWidth),
			y: 1 - MathUtils.getInterpolation(posOnImage.y, ratioOffset.y, ratioOffset.y + bgImageHeight),
		};

		const spaceOffset = this.props.spaceViewRenderer.spaceOffset;
		const NDCToWorldSpace = THREEUtils.NDCtoWorldCoordinates(
			posToNDC.x,
			posToNDC.y,
			this.props.spaceViewRenderer.spaceOffset.z,
			this.props.spaceViewRenderer.spaceSize,
		);

		this.props.spaceViewRenderer.toolManager.cameraControls.moveCameraTo(
			NDCToWorldSpace.x + spaceOffset.x,
			NDCToWorldSpace.y + spaceOffset.y,
			true,
			Constants.DURATIONS.DEFAULT_ANIMATION,
		);
	}

	private onMouseWheel = (event: React.WheelEvent) => {
		const direction = -Math.sign(event.deltaY); /** direction--> zoom-in: positive, zoom-out: negative */

		this.props.spaceViewRenderer.toolManager.cameraControls.zoomToDirection(direction);
	};

	private getSpaceToNavigationBoxRatio(background: HTMLElement) {
		const spaceSize = this.props.spaceViewRenderer.spaceSize;

		return spaceSize.width / spaceSize.height / (background.offsetWidth / background.offsetHeight);
	}

	private readonly forceUpdateArrow = () => {
		this.forceUpdate();
	};

	public override componentDidMount() {
		this.props.spaceViewRenderer.signals.spaceLoadReady.add(this.forceUpdateArrow);
	}

	public override componentWillUnmount() {
		this.props.spaceViewRenderer.signals.spaceLoadReady.remove(this.forceUpdateArrow);
	}

	public override render() {
		const {spaceViewRenderer} = this.props;
		const {themeType} = spaceViewRenderer;

		const bgStyle = {
			backgroundImage: `url(${this.props.navigationBoxImgSrc})`,
			filter: themeType === "light" ? "none" : "invert(1)",
		};

		const {bgImageWidth, bgImageHeight} = this.getBackgroundSize();

		return (
			<div className="NavigationBox">
				<div
					ref={this._background}
					className="navigationBackground"
					style={bgStyle}
					onWheel={this.onMouseWheel}
				>
					<NavigationCameraBoxV5
						spaceViewRenderer={spaceViewRenderer}
						domWidth={Constants.SIZE.NAVIGATION_BG.WIDTH}
						domHeight={Constants.SIZE.NAVIGATION_BG.HEIGHT}
						bgWidth={bgImageWidth}
						bgHeight={bgImageHeight}
						themeType={themeType}
					/>
				</div>
				<PointerDetectorReact
					onDown={this.onPointerDown}
					onMove={this.onPointerMove}
					onUp={this.onPointerUp}
					parent={null}
					refObject={this._background}
				/>
			</div>
		);
	}
}
